import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';

interface CookieWarningSignature {
  Element: HTMLDivElement;
  Args: {
    onClose: () => void;
    onRetry?: () => void;
  };
  Blocks: {};
}

type BrowserKey = 'chrome' | 'edge' | 'opera' | 'vivaldi';

export default class CookieWarningComponent extends Component<CookieWarningSignature> {
  // private helpHref = 'https://support.google.com/accounts/answer/61416';
  private authHost = 'bbmo-auth.singularsound.workers.dev';

  @tracked selectedBrowser: BrowserKey = 'chrome';

  constructor(owner: unknown, args: CookieWarningSignature['Args']) {
    super(owner, args);
    this.selectedBrowser = this.detectBrowser();
  }

  private detectBrowser(): BrowserKey {
    if (typeof navigator === 'undefined') return 'chrome';
    const ua = navigator.userAgent;
    if (/Vivaldi/i.test(ua)) return 'vivaldi';       // Vivaldi
    if (/Edg\//.test(ua)) return 'edge';             // Microsoft Edge (Chromium)
    if (/OPR\//.test(ua)) return 'opera';            // Opera
    // Default catch-all for Chromium/Chrome
    return 'chrome';
  }

  get authUrl() { return `https://${this.authHost}`; }
  get appOrigin() {
    if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
    return 'https://bbmo.singularsound.com';
  }
  
  get isChrome() { return this.selectedBrowser === 'chrome'; }
  get isEdge() { return this.selectedBrowser === 'edge'; }
  get isOpera() { return this.selectedBrowser === 'opera'; }
  get isVivaldi() { return this.selectedBrowser === 'vivaldi'; }

  @action close() { this.args.onClose?.(); }
  @action retry() { this.args.onRetry?.(); }
  // @action openHelp() { window.open(this.helpHref, '_blank', 'noopener,noreferrer'); }
  @action async copyLink(link: string) {
    try {
      await navigator.clipboard?.writeText(link);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      } catch {
        // ignore
      }
    }
  }
  @action chooseBrowser(evt: Event) {
    const val = (evt.target as HTMLSelectElement).value as BrowserKey;
    this.selectedBrowser = val;
  }

  <template>
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" role="dialog" aria-modal="true">
      <div class="w-full max-w-xl rounded-2xl bg-white text-gray-900 shadow-2xl ring-1 ring-black/10 dark:bg-primary-darkComponent dark:text-white">
        <div class="flex items-start justify-between gap-3 px-6 pt-5">
          <div>
            <h2 class="mt-1 text-2xl font-semibold leading-tight">Cookies are required to log in / sign up</h2>
          </div>
        </div>

        <div class="px-6 py-4 space-y-3 text-sm leading-relaxed">
          <p>
            We couldn't save a login cookie from our authentication service.
            Some browsers block third-party cookies, which prevents sign-in from working here.
          </p>
          <p>
            Please enable third-party cookies, or (if you prefer to keep that disabled) add an exception (as described in the instructions below), then click "Retry".
          </p>

          <div class  ="mt-2 space-y-2">
            <label class="block text-xs opacity-80">Choose your browser</label>
            <select
              class="mt-1 w-full rounded border border-gray-300 bg-white p-2 text-sm dark:bg-black/20 dark:border-white/20"
              style="background-color:#111827;color:#fff;"
              {{on "change" this.chooseBrowser}}
            >
              <option value="chrome" selected={{this.isChrome}} style="background-color:#111827;color:#fff;">Chrome</option>
              <option value="edge" selected={{this.isEdge}} style="background-color:#111827;color:#fff;">Microsoft Edge</option>
              <option value="opera" selected={{this.isOpera}} style="background-color:#111827;color:#fff;">Opera</option>
              <option value="vivaldi" selected={{this.isVivaldi}} style="background-color:#111827;color:#fff;">Vivaldi</option>
            </select>

            <div class="mt-2 text-sm">
              <strong>Instructions:</strong><br>
              {{#if this.isEdge}}
                Open edge://settings/privacy/cookies
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded px-2 py-1 bg-transparent border border-transparent text-blue-300 hover:text-white ml-1"
                  {{on "click" (fn this.copyLink "edge://settings/privacy/cookies")}}
                  title="Copy link"
                ><FaIcon @icon="clone" /></button><br>
                Toggle "Block third-party cookies" off, or keep blocking on and add an exception under "Allowed to save cookies" for {{this.authUrl}}
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded px-2 py-1 bg-transparent border border-transparent text-blue-300 hover:text-white ml-1"
                  {{on "click" (fn this.copyLink this.authUrl)}}
                  title="Copy site"
                ><FaIcon @icon="clone" /></button>
              {{else if this.isOpera}}
                Open opera://settings/cookies
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded px-2 py-1 bg-transparent border border-transparent text-blue-300 hover:text-white ml-1"
                  {{on "click" (fn this.copyLink "opera://settings/cookies")}}
                  title="Copy link"
                ><FaIcon @icon="clone" /></button><br>
                Select allow third-party cookies, or add a site exception for {{this.appOrigin}}
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded px-2 py-1 bg-transparent border border-transparent text-blue-300 hover:text-white ml-1"
                  {{on "click" (fn this.copyLink this.appOrigin)}}
                  title="Copy site"
                ><FaIcon @icon="clone" /></button>
              {{else if this.isVivaldi}}
                Open vivaldi://settings/privacy/
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded px-2 py-1 bg-transparent border border-transparent text-blue-300 hover:text-white ml-1"
                  {{on "click" (fn this.copyLink "vivaldi://settings/privacy/")}}
                  title="Copy link"
                ><FaIcon @icon="clone" /></button><br>
                Scroll down to "Cookies" and select "allow all" under "third-party cookies", or open chrome://settings/cookies
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded px-2 py-1 bg-transparent border border-transparent text-blue-300 hover:text-white ml-1"
                  {{on "click" (fn this.copyLink "chrome://settings/cookies")}}
                  title="Copy link"
                ><FaIcon @icon="clone" /></button>
                and add an exception under "Sites allowed to use third-party cookies" for {{this.appOrigin}}
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded px-2 py-1 bg-transparent border border-transparent text-blue-300 hover:text-white ml-1"
                  {{on "click" (fn this.copyLink this.appOrigin)}}
                  title="Copy site"
                ><FaIcon @icon="clone" /></button>
              {{else}}
                Open chrome://settings/cookies
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded px-2 py-1 bg-transparent border border-transparent text-blue-300 hover:text-white ml-1"
                  {{on "click" (fn this.copyLink "chrome://settings/cookies")}}
                  title="Copy link"
                ><FaIcon @icon="clone" /></button><br>
                Choose "Allow third-party cookies", or leave blocking on and add an exception under "Sites allowed to use third-party cookies" for {{this.appOrigin}}
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded px-2 py-1 bg-transparent border border-transparent text-blue-300 hover:text-white ml-1"
                  {{on "click" (fn this.copyLink this.appOrigin)}}
                  title="Copy site"
                ><FaIcon @icon="clone" /></button>
              {{/if}}
            </div>
          </div>

          <p class="mt-1 text-xs opacity-80">
            Private/Incognito windows or blocking extensions can still prevent cookies.
            Use a normal window if it still doesn't work in Private/Incognito.
          </p>
        </div>

        <div class="flex flex-col gap-3 px-6 pb-5 sm:flex-row sm:justify-end">
          {{!-- <button
            type="button"
            class="inline-flex justify-center rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:border-gray-400 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
            {{on "click" this.openHelp}}
          >
            How to enable cookies
          </button> --}}
          <button
            type="button"
            class="inline-flex justify-center rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:border-gray-400 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
            {{on "click" this.retry}}
          >
            Retry
          </button>
          <button
            type="button"
            class="inline-flex justify-center rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:border-gray-400 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
            {{on "click" this.close}}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    CookieWarning: typeof CookieWarningComponent;
  }
}
